import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Inbox, MapPin, Tag, Phone, ExternalLink, Loader2, Mail, ShoppingCart,
    CheckCircle2, Bike, Zap, Plus, X, UserCheck, Landmark, Megaphone,
    MessageSquare, CalendarClock, ChevronDown, ChevronUp, Globe, Pencil, AlertTriangle,
    Download, Upload, FileUp, MessageCircle, Sparkles, Send, Settings, BarChart3, Video,
} from "lucide-react";
import api, { getApiEmployer } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import LeadRulesModal from "../components/leads/LeadRulesModal";
import LeadTimeline, { LeadWorkflow } from "../components/leads/LeadTimeline";
import LeadPerformanceModal from "../components/leads/LeadPerformanceModal";
import CultureHintCard from "../components/culture/CultureHintCard";
import { SellerSelect } from "../components/leads/SellerSelect";
import { useGearsVisible } from "../lib/gearVisibility";

// Jul-2026 (User-Wunsch) — Herkunft-Kennzeichnung + Ampel-System
const ORIGIN_LABELS = [
    [(s) => s.startsWith("financing"), "Finanzierungsauslauf"],
    [(s) => s.startsWith("leasing"), "Leasingauslauf"],
    [(s) => s === "email_inbound", "Börse"],
    [(s) => s === "social_media", "Social Media"],
    [(s) => s === "homepage", "Eigene Homepage"],
    [(s) => s === "own_customers", "Eigene Kunden"],
];
const leadOrigin = (lead) => {
    const s = (lead.source || "").toLowerCase();
    for (const [test, label] of ORIGIN_LABELS) if (test(s)) return label;
    return null;
};
// Popup „Lead-Erinnerungen" entfernt (Jun-2026, User-Wunsch) — die Ampel wird
// direkt am Lead-Listing angezeigt, das Popup bot keinen Mehrwert.
const OUTCOME_OPTIONS = [
    ["not_reached", "Nicht erreicht"],
    ["no_response", "Keine Rückmeldung"],
    ["just_asking", "Wollte nur mal nachfragen"],
    ["will_come_back", "Meldet sich wieder"],
    ["test_ride", "Probefahrt vereinbart"],
];

const STATUS_LABELS = { assigned: "Zugewiesen", in_progress: "In Bearbeitung", won: "Gewonnen", lost: "Verloren", archived: "Archiviert" };
const STATUS_STYLE = {
    assigned: "bg-blue-100 text-blue-900 border-blue-300",
    in_progress: "bg-indigo-100 text-indigo-900 border-indigo-300",
    won: "bg-emerald-100 text-emerald-900 border-emerald-300",
    lost: "bg-gray-100 text-gray-600 border-gray-300",
    archived: "bg-gray-100 text-gray-500 border-gray-300",
};
// Verfügbare Statuswechsel (Workflow). „lost"/„archived" verlangen einen Schließ-Grund.
const fmtDe = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso || ""; } };

// Aug-2026 (User-Wunsch) — Herkunfts-Badge mit Börsen-Branding prominent oben
const BOERSE_BRANDS = [
    ["mobile", "mobile.de", "bg-[#F97316] text-white border-[#F97316]"],
    ["kleinanzeigen", "Kleinanzeigen", "bg-[#255f36] text-[#9CE800] border-[#255f36]"],
    ["autoscout", "AutoScout24", "bg-[#FFCC00] text-[#111827] border-[#FFCC00]"],
    ["ebay", "eBay", "bg-white text-[#e53238] border-gray-300"],
    ["1000ps", "1000PS", "bg-red-600 text-white border-red-600"],
    ["facebook", "Facebook", "bg-[#1877F2] text-white border-[#1877F2]"],
    ["twowheels", "TwoWheels", "bg-[#111827] text-[#FFCC00] border-[#111827]"],
];
function BoerseBadge({ lead }) {
    const src = (lead.boerse || "").trim();
    const email = !src && (lead.source || "").includes("email");
    const label = src || (email ? "E-Mail-Eingang" : null);
    if (!label) return null;
    const brand = BOERSE_BRANDS.find(([k]) => label.toLowerCase().includes(k));
    return (
        <span className={`inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wide px-2 py-0.5 rounded-sm border shadow-sm ${brand ? brand[2] : "bg-gray-100 text-gray-700 border-gray-300"}`}
            title={`Herkunft: ${label}`} data-testid={`dealer-lead-source-badge-${lead.id}`}>
            {email ? <Mail className="w-3 h-3" /> : <Globe className="w-3 h-3" />} {brand ? brand[1] : label}
        </span>
    );
}
// Aug-2026 (User-Wunsch) — Geschäftsart unübersehbar kennzeichnen: FINANZIERUNG vs. LEASING
const leadDealType = (lead) => {
    if (lead.deal_type) return lead.deal_type;
    const ct = lead.financing?.contract_type;
    if (ct === "LEAS") return "leasing";
    if (ct === "FIN") return "finanzierung";
    const s = (lead.source || "").toLowerCase();
    if (s.startsWith("financing")) return "finanzierung";
    if (s.startsWith("leasing")) return "leasing";
    return null;
};
function DealTypeBadge({ lead }) {
    const t = leadDealType(lead);
    if (!t) return null;
    const leas = t === "leasing";
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-sm border shadow-sm ${leas ? "bg-purple-700 text-white border-purple-700" : "bg-blue-700 text-white border-blue-700"}`}
            title={leas ? "LEASING-Geschäft — Leasingvertrag (Rückgabe/Übernahme), KEINE Finanzierung" : "FINANZIERUNGS-Geschäft — Ratenkredit/Finanzierung, KEIN Leasing"}
            data-testid={`dealer-lead-dealtype-${lead.id}`}>
            <Landmark className="w-3 h-3" /> {leas ? "Leasing" : "Finanzierung"}
        </span>
    );
}
const fmtEur = (v) => (typeof v === "number"
    ? new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v) : "—");

// Grund/Anlass (User-Wunsch Jul-2026): nicht nur Herkunft, sondern auch WARUM der Lead entstand.
const SOURCE_REASONS = {
    financing_expiry: "Finanzierung läuft aus",
    leasing_return: "Leasing läuft aus",
    email_inbound: "Börsen-Anfrage",
    csv_import: "CSV-Import",
    test_drive: "Probefahrt",
    manual_dealer: "Manuell erfasst",
};
const leadReason = (lead) => lead.reason || SOURCE_REASONS[lead.source] || null;

const CONDITION_BADGE = {
    Neufahrzeug: { label: "Neu", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", pool: true },
    Tageszulassung: { label: "TZ", cls: "bg-teal-100 text-teal-900 border-teal-300", pool: true },
    Vorführfahrzeug: { label: "Vorführer", cls: "bg-amber-100 text-amber-900 border-amber-300", pool: false },
    Gebrauchtfahrzeug: { label: "Gebraucht", cls: "bg-gray-100 text-gray-700 border-gray-300", pool: false },
};
function ConditionBadge({ condition, testid }) {
    const c = CONDITION_BADGE[condition];
    if (!c) return null;
    return (
        <span
            className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border ${c.cls}`}
            title={c.pool ? "Fällt in den gemeinsamen Bildpool (NW/TZ) — Bilder werden beim Umwandeln automatisch angehängt" : "Symbolfoto-Vorschläge beim Umwandeln"}
            data-testid={testid}
        >
            {c.label}{c.pool ? " · Bildpool" : ""}
        </span>
    );
}

function LeadInput({ label, k, value, onChange, type = "text", placeholder = "" }) {
    return (
        <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(k, e.target.value)}
                placeholder={placeholder}
                className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:border-[#111827] focus:outline-none"
                data-testid={`lead-field-${k}`}
            />
        </label>
    );
}

// Jul-2026 — Minuten menschenlesbar (42 Min / 3,5 Std / 2,1 Tage)
function fmtMinutes(m) {
    if (m == null) return "—";
    if (m < 60) return `${Math.round(m)} Min`;
    if (m < 60 * 24) return `${(m / 60).toFixed(1).replace(".", ",")} Std`;
    return `${(m / 1440).toFixed(1).replace(".", ",")} Tage`;
}

export default function DealerLeadsPage() {
    const { user, loading } = useAuth();
    const gearsOk = useGearsVisible();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mine = searchParams.get("mine") === "1";
    const [data, setData] = useState({ items: [], counts: {} });
    const [busy, setBusy] = useState(true);
    const [converting, setConverting] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [condFilter, setCondFilter] = useState("all");
    // Aug-2026 (User-Wunsch) — Universal-Suche über ALLE Felder des Leads
    const [searchAll, setSearchAll] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [siteFilter, setSiteFilter] = useState("all");
    // Jul-2026 (User-Wunsch) — Reaktionszeit je Standort (Hauptsitz-Sicht)
    const [siteStats, setSiteStats] = useState(null);
    useEffect(() => {
        api.get("/dealer/leads/site-response-stats").then((r) => setSiteStats(r.data)).catch(() => setSiteStats(null));
    }, []);
    const [employees, setEmployees] = useState([]);
    const [assigning, setAssigning] = useState("");
    // Aug-2026 (User-Wunsch) — nach „Anruf“ automatisch „Ereignis erfassen“ anbieten
    const [callPrompt, setCallPrompt] = useState(null);
    const [autoAssign, setAutoAssign] = useState(false);
    const [autoBusy, setAutoBusy] = useState(false);
    // Jun-2026 (User-Wunsch) — Nachfass-Erinnerung (Wiedervorlage) konfigurierbar
    const [fuCfg, setFuCfg] = useState({ days: 3, email: true, sms: false });
    const [fuBusy, setFuBusy] = useState(false);
    const [expanded, setExpanded] = useState("");
    void expanded; void setExpanded;
    const [assistantFor, setAssistantFor] = useState("");
    const [scoreExplainFor, setScoreExplainFor] = useState("");
    const [closeModal, setCloseModal] = useState(null); // { lead }
    const [showRules, setShowRules] = useState(false);
    const [showPerf, setShowPerf] = useState(false);
    const [showReportRcpts, setShowReportRcpts] = useState(false);
    void showReportRcpts; void setShowReportRcpts;
    const [showHandover, setShowHandover] = useState(false);
    const [openLead, setOpenLead] = useState("");
    // Aug-2026 (User-Wunsch) — Standort-Abgabe (nur abgeben, nie holen)
    const [transferTargets, setTransferTargets] = useState([]);
    const [transferFor, setTransferFor] = useState(null);
    const [transferTarget, setTransferTarget] = useState("");
    const [transferNote, setTransferNote] = useState("");
    const [transferBusy, setTransferBusy] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const r = await api.get(`/dealer/leads${mine ? "?mine=1" : ""}`);
            setData(r.data);
        } catch (e) { console.error(e); }
        finally { setBusy(false); }
    }, [mine]);

    useEffect(() => {
        if (loading) return;
        const isEmployeeContext = !!(user && user.role === "vermittler" && getApiEmployer());
        if (!user || (user.role !== "b2b" && user.role !== "admin" && !isEmployeeContext)) {
            // Jul-2026 (User-Wunsch): Deep-Link aus der Nachfass-Mail nicht verlieren —
            // ausgeloggt → zum Login mit Rücksprung auf genau diesen Vorgang.
            const leadParam = searchParams.get("lead");
            if (!user && leadParam) {
                navigate(`/login?next=${encodeURIComponent(`/dealer/leads?lead=${leadParam}`)}`);
            } else {
                navigate("/");
            }
            return;
        }
        load();
        api.get("/me/tasks/assignable-employees?context=sales").then((r) => setEmployees(r.data.items || [])).catch(() => {});
        api.get("/dealer/leads/transfer-targets").then((r) => setTransferTargets(r.data.items || [])).catch(() => {});
        if (user.role === "b2b") {
            api.get("/users/me/dealer-profile").then((r) => {
                setAutoAssign(!!r.data?.auto_assign_leads_enabled);
                setFuCfg({
                    days: r.data?.lead_followup_days ?? 3,
                    email: r.data?.lead_followup_reminder_email ?? true,
                    sms: !!r.data?.lead_followup_reminder_sms,
                });
            }).catch(() => {});
        }
    }, [user, loading, navigate, load]);

    const doTransfer = async () => {
        setTransferBusy(true);
        try {
            const r = await api.post(`/dealer/leads/${transferFor.id}/transfer-site`, {
                target_dealer_id: transferTarget, note: transferNote.trim() || undefined,
            });
            toast.success(`Lead an ${r.data.target_label} abgegeben`);
            setTransferFor(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Abgabe fehlgeschlagen"); }
        finally { setTransferBusy(false); }
    };

    const saveFollowupCfg = async (patch) => {
        const next = { ...fuCfg, ...patch };
        setFuCfg(next);
        setFuBusy(true);
        try {
            await api.patch("/users/me/dealer-profile", {
                lead_followup_days: Math.max(1, Math.min(30, Number(next.days) || 3)),
                lead_followup_reminder_email: next.email,
                lead_followup_reminder_sms: next.sms,
            });
            toast.success("Nachfass-Einstellungen gespeichert.");
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setFuBusy(false); }
    };

    const toggleAutoAssign = async () => {
        const next = !autoAssign;
        setAutoBusy(true);
        try {
            await api.patch("/users/me/dealer-profile", { auto_assign_leads_enabled: next });
            setAutoAssign(next);
            toast.success(next
                ? "Auto-Zuweisung aktiv: neue Leads gehen an den anwesenden Verkäufer mit der geringsten Last."
                : "Auto-Zuweisung deaktiviert.");
        } catch (e) { toast.error(e?.response?.data?.detail || "Konnte Einstellung nicht speichern"); }
        finally { setAutoBusy(false); }
    };

    // Jul-2026 (User-Wunsch): Direktlink aus der Nachfass-E-Mail (?lead=<id>)
    // → Vorgang automatisch aufklappen, hinscrollen und hervorheben.
    const focusLeadId = searchParams.get("lead") || "";
    useEffect(() => {
        if (!focusLeadId || busy || !data.items.length) return;
        setExpanded(focusLeadId);
        setOpenLead(focusLeadId);
        setAssistantFor(focusLeadId);
        setTimeout(() => {
            document.getElementById(`lead-${focusLeadId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
    }, [focusLeadId, busy, data.items.length]);

    const patchLead = (id, patch) => setData((p) => ({
        ...p,
        items: p.items.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    }));

    const saveQuality = async (lead, body, opts = {}) => {
        try {
            const r = await api.patch(`/dealer/leads/${lead.id}/status`, body);
            patchLead(lead.id, r.data.lead || body);
            if (!opts.silent) toast.success("Lead aktualisiert");
            return true;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aktualisierung fehlgeschlagen");
            return false;
        }
    };

    const changeStatus = async (lead, status) => {
        if (status === "lost") { setCloseModal({ lead }); return; }
        await saveQuality(lead, { status });
    };

    const assignSeller = async (lead, sellerId) => {
        setAssigning(lead.id);
        try {
            const r = await api.patch(`/dealer/leads/${lead.id}/assign`, { seller_id: sellerId || null });
            setData((p) => ({
                ...p,
                items: p.items.map((l) => l.id === lead.id
                    ? { ...l, assigned_seller_id: r.data.assigned_seller_id, assigned_seller_name: r.data.assigned_seller_name, seller_absence: r.data.seller_absence || null }
                    : l),
            }));
            toast.success(sellerId
                ? `Zugewiesen an ${r.data.assigned_seller_name}${r.data.notified ? " — Verkäufer wurde benachrichtigt" : ""}`
                : "Zuweisung aufgehoben");
            if (r.data.warning) toast.warning(r.data.warning, { duration: 6000 });
        } catch (e) { toast.error(e?.response?.data?.detail || "Zuweisung fehlgeschlagen"); }
        finally { setAssigning(""); }
    };

    // Aug-2026 (User-Wunsch) — Umverteilen-Schnellaktion: nächster freier Verkäufer
    const reassignFree = async (lead) => {
        setAssigning(lead.id);
        try {
            const r = await api.post(`/dealer/leads/${lead.id}/reassign-free`);
            setData((p) => ({
                ...p,
                items: p.items.map((l) => l.id === lead.id
                    ? { ...l, assigned_seller_id: r.data.assigned_seller_id, assigned_seller_name: r.data.assigned_seller_name, seller_absence: null, assignment_note: r.data.previous_seller_name ? `Umverteilt (Vertretung für ${r.data.previous_seller_name})` : l.assignment_note }
                    : l),
            }));
            toast.success(`Umverteilt an ${r.data.assigned_seller_name} — Verkäufer wurde benachrichtigt`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Kein freier Verkäufer verfügbar"); }
        finally { setAssigning(""); }
    };

    const setOutcome = async (lead, outcome) => {
        try {
            const r = await api.patch(`/dealer/leads/${lead.id}/outcome`, { outcome: outcome || null });
            patchLead(lead.id, { outcome: outcome || null, ampel: outcome ? null : lead.ampel });
            toast.success(outcome ? `Ereignis erfasst: ${r.data.outcome_label}` : "Ereignis zurückgesetzt");
        } catch (e) { toast.error(e?.response?.data?.detail || "Ereignis konnte nicht gespeichert werden"); }
    };

    // Aug-2026 (User-Wunsch) — Kundendaten-Extraktion läuft automatisch bei der
    // Lead-Erstellung (Backend); der manuelle „Neu auslesen“-Button ist entfallen.

    // Aug-2026 (User-Wunsch) — Lead auf-/zuklappen; beim Öffnen startet der KI-Coach automatisch
    const toggleLead = (lead) => {
        setOpenLead((p) => {
            const next = p === lead.id ? "" : lead.id;
            setAssistantFor(next || "");
            return next;
        });
    };

    // Aug-2026 (User-Wunsch) — „Ereignis erfassen": Ergebnis ODER Status in einem Dropdown;
    // nach jeder Auswahl öffnen sich automatisch Vermerke & Wiedervorlage.
    const handleEvent = async (lead, val) => {
        if (val === "action:wiedervorlage") {
            setOpenLead(lead.id);
            setAssistantFor(lead.id);
            return;
        }
        if (val.startsWith("status:")) await changeStatus(lead, val.slice(7));
        else await setOutcome(lead, val);
        setOpenLead(lead.id);
        setAssistantFor(lead.id);
    };

    const exportCsv = async () => {
        setExporting(true);
        try {
            const r = await api.get("/dealer/leads/export.csv", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = "Leads.csv";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Leads als CSV exportiert");
        } catch (e) { toast.error(e?.response?.data?.detail || "Export fehlgeschlagen"); }
        finally { setExporting(false); }
    };

    const convert = async (lead) => {
        // Finanzierungs-Leads: Fahrzeug gehört bereits dem Kunden — keine Datenübernahme,
        // direkt zur Erfassung einer neuen Bestellung springen (User-Wunsch Jul-2026).
        if ((lead.source || "").startsWith("financing")) {
            toast.info("Fahrzeug bleibt beim Kunden — neue Bestellung erfassen.");
            navigate(`/create-listing?from_lead=${lead.id}`);
            return;
        }
        setConverting(lead.id);
        try {
            const r = await api.post(`/leads/${lead.id}/convert`);
            toast.success(r.data.already ? "Entwurf bereits vorhanden — wird geöffnet" : "Inserat-Entwurf angelegt");
            navigate(`/edit-listing/${r.data.listing_id}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Umwandlung fehlgeschlagen"); }
        finally { setConverting(""); }
    };

    if (loading || !user) return <div className="p-12">Lädt…</div>;
    const counts = data.counts || {};
    const condCounts = (data.items || []).reduce((acc, l) => {
        const c = (l.vehicle || {}).condition || "—";
        acc[c] = (acc[c] || 0) + 1; return acc;
    }, {});
    const CONDITION_FILTERS = [
        ["all", "Alle"],
        ["Neufahrzeug", "Neu"],
        ["Tageszulassung", "TZ"],
        ["Vorführfahrzeug", "Vorführer"],
        ["Gebrauchtfahrzeug", "Gebraucht"],
    ];
    const visibleItems = (condFilter === "all"
        ? data.items
        : data.items.filter((l) => ((l.vehicle || {}).condition) === condFilter)
    ).filter((l) => siteFilter === "all" || l.assigned_dealer_id === siteFilter)
        // Aug-2026 (User-Wunsch) — KPI-Kacheln als Filter; Verlorene standardmäßig ausgeblendet
        .filter((l) => {
            if (statusFilter === "all") return !["lost", "archived"].includes(l.status);
            if (statusFilter === "lost") return ["lost", "archived"].includes(l.status);
            return l.status === statusFilter;
        })
        // Aug-2026 (User-Wunsch) — Universal-Suche: durchsucht ALLE Felder
        .filter((l) => {
            const t = searchAll.trim().toLowerCase();
            if (!t) return true;
            try { return JSON.stringify(l).toLowerCase().includes(t); } catch { return true; }
        });
    // Jul-2026 (User-Wunsch) — Eskalierte Leads gebündelt: trotz Wiedervorlage-
    // Erinnerung weiterhin ohne Erstkontakt (Alert ging an den Hauptsitz).
    const escalatedLeads = data.items.filter((l) =>
        l.no_contact_escalated_at && !l.first_contact_at && ["assigned", "in_progress"].includes(l.status));
    const jumpToLead = (id) => {
        setCondFilter("all");
        setSiteFilter("all");
        const target = data.items.find((l) => l.id === id);
        setStatusFilter(target && ["lost", "archived"].includes(target.status) ? "lost" : "all");
        setExpanded(id);
        setTimeout(() => document.getElementById(`lead-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    };
    // Jul-2026 (User-Wunsch) — Standort-Filter am Hauptsitz (nur wenn mehrere Standorte Leads haben)
    const siteOptions = Object.values(data.items.reduce((acc, l) => {
        if (l.assigned_dealer_id && l.assigned_site_label) acc[l.assigned_dealer_id] = { id: l.assigned_dealer_id, label: l.assigned_site_label };
        return acc;
    }, {}));

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="dealer-leads-page">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-[#111827] flex items-center gap-2">
                        <Inbox className="w-7 h-7 text-[#FFCC00]" /> Meine Leads
                        {user.role === "b2b" && (
                            <>
                                {gearsOk && <button onClick={() => setShowRules(true)}
                                    className="ml-1 p-2 rounded-sm border border-gray-300 text-gray-500 hover:text-[#111827] hover:border-[#111827] transition-colors"
                                    title="Lead-Regeln für Verkäufer (Herkunft, Ampel, Verteilung, Automatisierung, Wochenreport)"
                                    data-testid="lead-rules-gear">
                                    <Settings className="w-5 h-5" />
                                </button>}
                            </>
                        )}
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {mine
                            ? "Die Ihnen persönlich zugewiesenen Leads — mit einem Klick in einen Inserat-Entwurf umwandeln."
                            : "Ihnen zugewiesene Angebote aus Autobörsen — mit einem Klick in einen Inserat-Entwurf / Bestand umwandeln. Über das Auswahlfeld lassen sich Leads an Mitarbeiter zuweisen."}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <button onClick={exportCsv} disabled={exporting} className="inline-flex items-center gap-2 border-2 border-[#111827] text-[#111827] rounded-sm px-3 py-2 text-sm font-bold hover:bg-gray-50 disabled:opacity-60" data-testid="dealer-leads-export-btn">
                        {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CSV-Export
                    </button>
                    <button onClick={() => setShowImport(true)} className="inline-flex items-center gap-2 border-2 border-[#111827] text-[#111827] rounded-sm px-3 py-2 text-sm font-bold hover:bg-gray-50" data-testid="dealer-leads-import-btn">
                        <Upload className="w-4 h-4" /> CSV-Import
                    </button>
                    {/* Aug-2026 (User-Wunsch) — Wochenreport & Empfänger sind ins Zahnrad (Lead-Regeln) umgezogen */}
                    {user.role === "b2b" && (
                        <button onClick={() => setShowPerf(true)}
                            className="inline-flex items-center gap-2 border-2 border-[#111827] text-[#111827] rounded-sm px-3 py-2 text-sm font-bold hover:bg-gray-50"
                            title="Erfolgsreport: Reaktionszeit & Ergebnis je Verkäufer"
                            data-testid="lead-perf-btn">
                            <BarChart3 className="w-4 h-4" /> Erfolgsreport
                        </button>
                    )}
                    <button
                        onClick={() => setShowHandover(true)}
                        className="inline-flex items-center gap-2 border-2 border-[#111827] text-[#111827] rounded-sm px-3 py-2 text-sm font-bold hover:bg-gray-50"
                        title="Urlaubs-Vertretung: alle offenen Leads eines Verkäufers gesammelt an eine Vertretung übergeben"
                        data-testid="dealer-leads-handover-btn"
                    >
                        <CalendarClock className="w-4 h-4" /> Urlaubs-Vertretung
                    </button>
                    <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-orange-500 text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-orange-600 animate-pulse hover:animate-none shadow-md shadow-orange-300/50" data-testid="dealer-lead-create-btn">
                        <Plus className="w-4 h-4" /> Laden-Kunden erfassen
                    </button>
                </div>
            </div>

            {user.role === "b2b" && showRules && (
                <LeadRulesModal
                    onClose={() => setShowRules(false)}
                    autoAssign={autoAssign}
                    onToggleAutoAssign={toggleAutoAssign}
                    autoBusy={autoBusy}
                    fuCfg={fuCfg}
                    onSaveFollowup={saveFollowupCfg}
                    fuBusy={fuBusy}
                />
            )}

            {user.role === "b2b" && showPerf && (
                <LeadPerformanceModal onClose={() => setShowPerf(false)} />
            )}

            {/* Jul-2026 (User-Wunsch) — Firmen-DNA: Leitfaden zur Lead-Bearbeitung. */}
            <CultureHintCard area="leads" />

            {escalatedLeads.length > 0 && (
                <div className="mb-6 border-2 border-red-300 bg-red-50 rounded-sm p-4" data-testid="dealer-leads-escalations">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <h2 className="text-sm font-bold text-red-800 uppercase tracking-wider">
                            Eskalierte Leads ({escalatedLeads.length}) — trotz Erinnerung ohne Erstkontakt
                        </h2>
                    </div>
                    <ul className="space-y-1.5">
                        {escalatedLeads.map((l) => {
                            const veh = l.vehicle || {};
                            const label = [veh.brand, veh.model].filter(Boolean).join(" ") || l.listing_title || l.subject || "Anfrage";
                            const days = l.created_at ? Math.floor((Date.now() - new Date(l.created_at)) / 86400000) : null;
                            return (
                                <li key={l.id}>
                                    <button type="button" onClick={() => jumpToLead(l.id)}
                                        className="w-full text-left flex flex-wrap items-center gap-2 bg-white border border-red-200 rounded-sm px-2.5 py-1.5 hover:bg-red-100 transition-colors"
                                        title="Zum Lead springen"
                                        data-testid={`escalated-lead-${l.id}`}>
                                        <span className="font-bold text-sm text-gray-900">{label}</span>
                                        {(l.seller || {}).name && <span className="text-xs text-gray-500">· {(l.seller || {}).name}</span>}
                                        {l.assigned_site_label && (
                                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-sky-50 text-sky-800 border border-sky-200">
                                                <MapPin className="w-3 h-3" /> {l.assigned_site_label}
                                            </span>
                                        )}
                                        {l.assigned_seller_name && (
                                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200">
                                                <UserCheck className="w-3 h-3" /> {l.assigned_seller_name}
                                            </span>
                                        )}
                                        {days != null && (
                                            <span className="ml-auto text-[10px] font-bold text-red-700">
                                                {days} Tage ohne Erstkontakt
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[["assigned", "Neu zugewiesen"], ["in_progress", "In Bearbeitung"], ["won", "Gewonnen"], ["lost", "Verloren"]].map(([k, label]) => {
                    const active = statusFilter === k;
                    return (
                        <button key={k} type="button"
                            onClick={() => setStatusFilter((p) => (p === k ? "all" : k))}
                            className={`text-left bg-white border rounded-sm p-4 transition-colors ${active ? "border-[#111827] ring-2 ring-[#FFCC00]" : "border-gray-200 hover:border-[#111827]"}`}
                            title={k === "lost" ? "Verlorene Leads sind standardmäßig ausgeblendet — Klick zeigt sie an" : `Nur „${label}" anzeigen (erneut klicken = Filter aus)`}
                            data-testid={`dealer-leads-kpi-${k}`}>
                            <div className="text-3xl font-black font-mono-num text-[#111827]">{counts[k] ?? 0}</div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mt-1">{label}{active ? " ✓" : ""}</div>
                        </button>
                    );
                })}
            </div>

            {data.items.length > 0 && (
                <div className="relative mb-3 max-w-xl" data-testid="dealer-leads-search-wrap">
                    <input
                        value={searchAll}
                        onChange={(e) => setSearchAll(e.target.value)}
                        placeholder="Universal-Suche: Name, E-Mail, Telefon, Fahrzeug, Interne Nr., Betreff, Notizen …"
                        className="w-full border border-gray-300 rounded-sm pl-3 pr-16 py-2 text-sm bg-white focus:outline-none focus:border-[#111827]"
                        data-testid="dealer-leads-universal-search"
                    />
                    {searchAll && (
                        <button onClick={() => setSearchAll("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase text-gray-400 hover:text-gray-700"
                            data-testid="dealer-leads-search-clear">
                            Leeren
                        </button>
                    )}
                </div>
            )}

            {data.items.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="dealer-leads-cond-filter">
                    {CONDITION_FILTERS.map(([val, label]) => {
                        const active = condFilter === val;
                        const n = val === "all" ? data.items.length : (condCounts[val] || 0);
                        return (
                            <button
                                key={val}
                                onClick={() => setCondFilter(val)}
                                className={`text-xs font-bold rounded-sm border px-3 py-1.5 transition-colors ${active ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-[#111827] border-gray-300 hover:border-[#111827]"}`}
                                data-testid={`dealer-leads-cond-${val}`}
                            >
                                {label} <span className={active ? "text-[#FFCC00]" : "text-gray-400"}>({n})</span>
                            </button>
                        );
                    })}
                    {/* Jul-2026 (User-Wunsch) — Standort-Filter (Hauptsitz-Sicht) */}
                    {siteOptions.length > 1 && (
                        <select
                            value={siteFilter}
                            onChange={(e) => setSiteFilter(e.target.value)}
                            className="text-xs font-bold rounded-sm border border-gray-300 bg-white px-2 py-1.5 max-w-[280px]"
                            data-testid="dealer-leads-site-filter"
                        >
                            <option value="all">Alle Standorte ({data.items.length})</option>
                            {siteOptions.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.label} ({data.items.filter((l) => l.assigned_dealer_id === s.id).length})
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            )}

            {/* Jul-2026 (User-Wunsch) — Reaktionszeit je Standort (Hauptsitz) */}
            {siteStats?.sites?.length > 1 && (
                <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4" data-testid="dealer-leads-response-stats">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                        Reaktionszeit je Standort — Erstkontakt nach Lead-Eingang (letzte {siteStats.days} Tage)
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                            <thead>
                                <tr className="text-left text-gray-400 uppercase tracking-wider text-[9px]">
                                    <th className="py-1 pr-3">Standort</th>
                                    <th className="py-1 pr-3">Leads</th>
                                    <th className="py-1 pr-3">Ø Antwortzeit</th>
                                    <th className="py-1 pr-3">Median</th>
                                    <th className="py-1 pr-3">Kontaktquote</th>
                                    <th className="py-1">Unkontaktiert</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {siteStats.sites.map((s, i) => (
                                    <tr key={s.dealer_id} data-testid={`response-stats-row-${i}`}>
                                        <td className="py-1.5 pr-3 font-bold">{s.site_label}</td>
                                        <td className="py-1.5 pr-3 font-mono-num">{s.leads}</td>
                                        <td className="py-1.5 pr-3 font-bold">{fmtMinutes(s.avg_minutes)}</td>
                                        <td className="py-1.5 pr-3">{fmtMinutes(s.median_minutes)}</td>
                                        <td className="py-1.5 pr-3">
                                            <span className={`font-bold ${s.contact_quote >= 80 ? "text-emerald-700" : s.contact_quote >= 50 ? "text-amber-700" : "text-red-700"}`}>{s.contact_quote}%</span>
                                        </td>
                                        <td className="py-1.5">
                                            {s.open_uncontacted > 0
                                                ? <span className="font-bold text-red-700">{s.open_uncontacted} offen</span>
                                                : <span className="text-emerald-700">✓ alle kontaktiert</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {busy ? (
                <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : data.items.length === 0 ? (
                <div className="text-center text-gray-500 py-16 border border-dashed border-gray-300 rounded-sm" data-testid="dealer-leads-empty">
                    <div>Aktuell sind Ihnen keine Leads zugewiesen.</div>
                    <LeadsEmptyDiagnostics />
                </div>
            ) : visibleItems.length === 0 ? (
                <div className="text-center text-gray-500 py-12 border border-dashed border-gray-300 rounded-sm" data-testid="dealer-leads-filter-empty">
                    Keine Leads im Zustand „{(CONDITION_FILTERS.find(([v]) => v === condFilter) || [])[1]}".
                </div>
            ) : (
                <div className="space-y-3" data-testid="dealer-leads-list">
                    {visibleItems.map((lead) => {
                        const v = lead.vehicle || {};
                        const loc = lead.location || {};
                        const converted = !!lead.converted_listing_id;
                        const open = openLead === lead.id;
                        return (
                            <div key={lead.id} id={`lead-${lead.id}`} className={`bg-white border rounded-sm p-4 ${focusLeadId === lead.id ? "border-[#FFCC00] ring-2 ring-[#FFCC00]/50" : "border-gray-200"}`} data-testid={`dealer-lead-${lead.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="cursor-pointer select-none" onClick={() => toggleLead(lead)}
                                            title={open ? "Lead zuklappen" : "Lead öffnen — KI-Coach & Verlauf starten automatisch"}
                                            data-testid={`dealer-lead-toggle-${lead.id}`}>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <BoerseBadge lead={lead} />
                                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border ${STATUS_STYLE[lead.status]}`}>{lead.status === "assigned" ? (lead.assigned_seller_id ? "Zugewiesen" : "Neu") : (STATUS_LABELS[lead.status] || lead.status)}</span>
                                            {lead.score_info?.grade && lead.score_info.grade !== "-" && (
                                                <button type="button"
                                                    onClick={(e) => { e.stopPropagation(); setScoreExplainFor((p) => (p === lead.id ? "" : lead.id)); }}
                                                    title="Klicken für Score-Erklärung"
                                                    className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-sm border cursor-pointer hover:brightness-95 ${lead.score_info.grade === "A" ? "bg-emerald-600 text-white border-emerald-600" : lead.score_info.grade === "B" ? "bg-amber-400 text-[#111827] border-amber-400" : "bg-red-600 text-white border-red-600"}`}
                                                    data-testid={`dealer-lead-score-${lead.id}`}>
                                                    Score {lead.score_info.grade} · {lead.score_info.score}
                                                </button>
                                            )}
                                            <DealTypeBadge lead={lead} />
                                            {lead.repeat_buyer?.count > 0 && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-sm bg-amber-400 text-amber-950 border border-amber-500 shadow-sm"
                                                    title={`Bestandskunde: ${lead.repeat_buyer.count} Kauf/Käufe bei uns — zuletzt ${lead.repeat_buyer.last_vehicle || "Fahrzeug"}${lead.repeat_buyer.last_at ? ` am ${new Date(lead.repeat_buyer.last_at).toLocaleDateString("de-DE")}` : ""}`}
                                                    data-testid={`dealer-lead-repeat-${lead.id}`}>
                                                    <UserCheck className="w-3 h-3" /> Wiederkäufer · {lead.repeat_buyer.count}
                                                </span>
                                            )}
                                            {leadReason(lead) && (
                                                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border border-indigo-300" title="Grund / Anlass" data-testid={`dealer-lead-reason-${lead.id}`}>
                                                    {leadReason(lead)}
                                                </span>
                                            )}
                                            {lead.bank && (
                                                <span className="inline-flex items-center bg-white border border-gray-200 rounded-sm px-1.5 py-0.5" title={lead.bank} data-testid={`dealer-lead-bank-logo-${lead.id}`}>
                                                    <img src={lead.bank.toLowerCase().includes("renault") ? "/banks/renault-bank.svg" : "/banks/santander.svg"} alt={lead.bank} className="h-5 w-auto" />
                                                </span>
                                            )}
                                            <ConditionBadge condition={v.condition} testid={`dealer-lead-condition-${lead.id}`} />
                                            {lead.auto_assigned && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border border-indigo-300"><Zap className="w-3 h-3" /> Auto</span>}
                                            {lead.assigned_site_label && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-sky-50 text-sky-800 border border-sky-200" title="Zugewiesener Standort" data-testid={`dealer-lead-site-badge-${lead.id}`}><MapPin className="w-3 h-3" /> {lead.assigned_site_label}</span>}
                                            {/* Aug-2026 (User-Wunsch) — Zuweisung im Kopf sichtbar, auch eingeklappt */}
                                            <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                <UserCheck className={`w-3 h-3 ${lead.assigned_seller_id ? "text-emerald-600" : "text-gray-400"}`} />
                                                <SellerSelect
                                                    employees={employees}
                                                    value={lead.assigned_seller_id || ""}
                                                    disabled={assigning === lead.id}
                                                    onChange={(v) => assignSeller(lead, v)}
                                                    className={`text-[10px] font-bold rounded-sm border px-1 py-0.5 max-w-[170px] focus:outline-none disabled:opacity-60 ${lead.assigned_seller_id ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-white text-gray-500 border-gray-300"}`}
                                                    testid={`dealer-lead-assign-${lead.id}`}
                                                />
                                                {lead.auto_assigned_seller ? <span className="text-[9px] font-bold text-emerald-700">auto</span> : null}
                                            </span>
                                            {transferTargets.some((t) => t.id !== lead.assigned_dealer_id) && (lead.status === "assigned" || lead.status === "in_progress") && (
                                                <button type="button"
                                                    onClick={(e) => { e.stopPropagation(); setTransferFor(lead); setTransferTarget(""); setTransferNote(""); }}
                                                    className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm border border-gray-300 bg-white text-gray-600 hover:border-[#111827] hover:text-[#111827]"
                                                    title="Lead an einen anderen Standort abgeben (nur abgeben — kein Holen von fremden Standorten)"
                                                    data-testid={`dealer-lead-transfer-${lead.id}`}>
                                                    <MapPin className="w-3 h-3" /> Standort abgeben
                                                </button>
                                            )}
                                            {lead.seller_absence && (
                                                <>
                                                <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm border ${lead.seller_absence.absent ? "bg-amber-50 text-amber-900 border-amber-300" : "bg-orange-50 text-orange-800 border-orange-200"}`}
                                                    title={lead.seller_absence.absent
                                                        ? `Der zugewiesene Verkäufer ist abwesend — Lead kann frühestens ab ${fmtDe(lead.seller_absence.back_on)} durch ihn weiterbearbeitet werden. Ggf. anderem Verkäufer zuweisen.`
                                                        : `Der zugewiesene Verkäufer ist demnächst abwesend (${fmtDe(lead.seller_absence.from)}–${fmtDe(lead.seller_absence.until)}).`}
                                                    data-testid={`dealer-lead-seller-absence-${lead.id}`}>
                                                    <CalendarClock className="w-3 h-3" />
                                                    {lead.seller_absence.absent
                                                        ? `${lead.seller_absence.label} bis ${fmtDe(lead.seller_absence.until)} · wieder da ${fmtDe(lead.seller_absence.back_on)}`
                                                        : `${lead.seller_absence.label} ab ${fmtDe(lead.seller_absence.from)}`}
                                                </span>
                                                {lead.seller_absence.absent && (lead.status === "assigned" || lead.status === "in_progress") && (
                                                    <button type="button"
                                                        onClick={(e) => { e.stopPropagation(); reassignFree(lead); }}
                                                        disabled={assigning === lead.id}
                                                        className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-[#111827] text-white hover:bg-black disabled:opacity-50"
                                                        title="Lead mit einem Klick dem nächsten freien (anwesenden) Verkäufer mit der geringsten Last zuweisen"
                                                        data-testid={`dealer-lead-reassign-free-${lead.id}`}>
                                                        {assigning === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} An freien Verkäufer
                                                    </button>
                                                )}
                                                </>
                                            )}
                                            {lead.follow_up_date && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200" data-testid={`dealer-lead-followup-badge-${lead.id}`}><CalendarClock className="w-3 h-3" /> {lead.follow_up_date}</span>}
                                        </div>
                                        <div className="text-lg font-black text-[#111827] mt-1 flex items-center gap-2">
                                            <Bike className="w-4 h-4 text-gray-400" /> {[v.brand, v.model].filter(Boolean).join(" ") || lead.subject}
                                        </div>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                                            {lead.seller?.name && <span className="inline-flex items-center gap-1 font-bold text-[#111827]" data-testid={`dealer-lead-customer-name-${lead.id}`}><UserCheck className="w-3.5 h-3.5 text-gray-400" /> {lead.seller.name}</span>}
                                            {typeof v.price === "number" && v.price > 0 && <span className="inline-flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> {fmtEur(v.price)}</span>}
                                            {v.year && <span>EZ {v.year}</span>}
                                            {typeof v.mileage === "number" && <span>{v.mileage.toLocaleString("de-DE")} km</span>}
                                            {(loc.zip || loc.city) && <span className="inline-flex items-center gap-1" data-testid={`dealer-lead-location-${lead.id}`}><MapPin className="w-3.5 h-3.5" /> {[loc.zip, loc.city].filter(Boolean).join(" ")}</span>}
                                            {lead.seller?.phone && <a href={`tel:${lead.seller.phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-blue-700"><Phone className="w-3.5 h-3.5" /> {lead.seller.phone}</a>}
                                            {lead.from_email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {lead.from_email}</span>}
                                            {lead.url && <a href={lead.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-blue-700"><ExternalLink className="w-3.5 h-3.5" /> Inserat</a>}
                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-400 ml-auto">
                                                {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                {open ? "Zuklappen" : "Details & KI-Coach"}
                                            </span>
                                        </div>
                                        </div>
                                        {open && (<>
                                        {scoreExplainFor === lead.id && lead.score_info && (
                                            <div className="mt-2 border-l-2 border-gray-300 pl-2.5 py-1 text-xs space-y-1" data-testid={`dealer-lead-score-explain-${lead.id}`}>
                                                <div className="font-bold">Score {lead.score_info.score}/100 — Stufe {lead.score_info.grade}: {lead.score_info.grade === "A" ? "heißer Lead, sofort kontaktieren" : lead.score_info.grade === "B" ? "warmer Lead, zeitnah nachfassen" : "kalter Lead, geringe Priorität"}</div>
                                                {(lead.score_info.hints || []).map((h, i) => <div key={i}>• {h}</div>)}
                                                <div className="text-gray-500">Ampel: <span className="font-bold text-emerald-600">Grün = A (≥ 70)</span> · <span className="font-bold text-amber-500">Gelb = B (≥ 40)</span> · <span className="font-bold text-red-600">Rot = C (&lt; 40)</span> — bewertet Aktualität, Erreichbarkeit (Telefon), Wiedervorlage und Finanzierungsende.</div>
                                            </div>
                                        )}
                                        {lead.score_info?.hints?.[0] && (lead.status === "assigned" || lead.status === "in_progress") && (
                                            <div className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 mt-1" data-testid={`dealer-lead-score-hint-${lead.id}`}>
                                                <Zap className="w-3 h-3" /> {lead.score_info.hints[0]}
                                            </div>
                                        )}
                                        {lead.financing && (
                                            <div className={`mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-l-2 pl-2.5 py-1 text-xs ${lead.financing.contract_type === "LEAS" ? "border-purple-400" : "border-blue-400"}`} data-testid={`dealer-lead-financing-${lead.id}`}>
                                                <span className={`inline-flex items-center gap-1 font-black uppercase tracking-wider ${lead.financing.contract_type === "LEAS" ? "text-purple-800" : "text-blue-800"}`}>
                                                    <Landmark className="w-3.5 h-3.5" /> {lead.financing.contract_type === "LEAS" ? "Leasing" : "Finanzierung"}{lead.financing.contract_number ? ` · ${lead.financing.contract_number}` : ""}
                                                </span>
                                                {typeof lead.financing.rate === "number" && <span>Monatliche Rate: <b className="font-mono-num">{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(lead.financing.rate)}</b></span>}
                                                {typeof lead.financing.balloon === "number" && <span>Restrate/Ballon: <b className="font-mono-num">{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(lead.financing.balloon)}</b></span>}
                                                {lead.financing.last_rate_date && <span>Letzte Rate am: <b>{new Date(lead.financing.last_rate_date).toLocaleDateString("de-DE")}</b></span>}
                                                {lead.financing.refinance_hint && (
                                                    <span className="inline-flex items-center gap-1 font-bold text-emerald-900 bg-emerald-100 border border-emerald-300 rounded-sm px-2 py-0.5" title="Hohe Schlussrate/Ballon — Anschluss- bzw. Nachfinanzierung anbieten und Zins-Provision erwirtschaften" data-testid={`dealer-lead-refinance-${lead.id}`}>
                                                        <Sparkles className="w-3 h-3" /> Nachfinanzierung anbieten (hohe Schlussrate)
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {Array.isArray(lead.handover_log) && lead.handover_log.length > 0 && (
                                            <details className="mt-1.5 text-xs" data-testid={`dealer-lead-handover-log-${lead.id}`}>
                                                <summary className="cursor-pointer font-bold text-gray-500 hover:text-gray-800 select-none">
                                                    Vertretungs-Historie ({lead.handover_log.length})
                                                </summary>
                                                <ul className="mt-1 space-y-1 pl-3 border-l-2 border-gray-200">
                                                    {lead.handover_log.slice().reverse().map((h, i) => (
                                                        <li key={i} className="text-gray-600" data-testid={`handover-log-entry-${lead.id}-${i}`}>
                                                            <span className="font-mono-num text-gray-400">{fmtDe(h.at)}</span>{" — "}
                                                            {h.type === "return"
                                                                ? <>zurück an <b>{h.to_name}</b> (Vertretung: {h.from_name}{h.activity ? <> · in der Zeit: <i>{h.activity}</i></> : null})</>
                                                                : <>{h.auto ? "automatisch " : ""}übergeben von <b>{h.from_name || "—"}</b> an <b>{h.to_name}</b>{h.note ? ` · ${h.note}` : ""}</>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </details>
                                        )}
                                        {lead.raw_text && null}
                                        <ContactButtons lead={lead} onCall={() => setCallPrompt(lead)} />
                                        <div className="mt-2">
                                            <LeadWorkflow lead={lead}
                                                before={
                                                    <button type="button" onClick={() => setAssistantFor((p) => (p === lead.id ? "" : lead.id))}
                                                        className={`flex items-center gap-1.5 rounded-sm px-2 py-1 text-[11px] font-bold border transition-colors whitespace-nowrap ${assistantFor === lead.id ? "bg-[#111827] border-[#111827] text-[#FFCC00]" : "bg-white border-[#111827] text-[#111827] hover:bg-gray-50"}`}
                                                        title="KI-Coach: Einschätzung, Einwandsbehandlung & kompletter Kommunikationsverlauf"
                                                        data-testid={`dealer-lead-assistant-${lead.id}`}>
                                                        <Sparkles className="w-3 h-3" /> KI-Coach
                                                    </button>
                                                }
                                            />
                                        </div>
                                        </>)}
                                    </div>
                                </div>
                                {open && assistantFor === lead.id && (
                                    <LeadTimeline leadId={lead.id} lead={lead}
                                        footerActions={<>
                                            {converted ? (
                                                <button onClick={() => navigate(`/edit-listing/${lead.converted_listing_id}`)}
                                                    className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                                    data-testid={`dealer-lead-open-draft-${lead.id}`}>
                                                    <CheckCircle2 className="w-3 h-3" /> Entwurf öffnen
                                                </button>
                                            ) : (
                                                <button onClick={() => convert(lead)} disabled={converting === lead.id}
                                                    className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-sm bg-[#FFCC00] text-[#111827] hover:brightness-95 disabled:opacity-60"
                                                    data-testid={`dealer-lead-convert-${lead.id}`}>
                                                    {converting === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" />} {(lead.source || "").startsWith("financing") ? "Neue Bestellung erfassen" : "GW-Berechnen"}
                                                </button>
                                            )}
                                            <button onClick={() => navigate("/dealer/disposition")}
                                                className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                                                title="Disposition — Fahrzeugbestand ansehen"
                                                data-testid={`dealer-lead-dispo-${lead.id}`}>
                                                <Bike className="w-3 h-3" /> Disposition
                                            </button>
                                            {(lead.status === "assigned" || lead.status === "in_progress") && (
                                                <select
                                                    value={lead.outcome || ""}
                                                    onChange={(e) => handleEvent(lead, e.target.value)}
                                                    className="text-[11px] font-bold rounded-sm border border-gray-300 bg-white px-2 py-1 focus:border-[#111827] focus:outline-none"
                                                    title="Ereignis erfassen: Telefonat-Ergebnis oder Status"
                                                    data-testid={`dealer-lead-outcome-${lead.id}`}
                                                >
                                                    <option value="">Ereignis erfassen…</option>
                                                    <optgroup label="Telefonat-Ergebnis">
                                                        {OUTCOME_OPTIONS.map(([k, label]) => (
                                                            <option key={k} value={k}>{label}</option>
                                                        ))}
                                                    </optgroup>
                                                    <optgroup label="Status setzen">
                                                        <option value="status:won">Gewonnen</option>
                                                        <option value="status:lost">Verloren…</option>
                                                    </optgroup>
                                                    <optgroup label="Aktionen">
                                                        <option value="action:wiedervorlage">Wiedervorlage setzen…</option>
                                                    </optgroup>
                                                </select>
                                            )}
                                            <span className="inline-flex items-center gap-1.5 ml-auto">
                                                <UserCheck className={`w-3.5 h-3.5 ${lead.assigned_seller_id ? "text-emerald-600" : "text-gray-400"}`} />
                                                <SellerSelect
                                                    employees={employees}
                                                    value={lead.assigned_seller_id || ""}
                                                    disabled={assigning === lead.id}
                                                    onChange={(v) => assignSeller(lead, v)}
                                                    className="text-[11px] font-bold rounded-sm border border-gray-300 bg-white px-2 py-1 max-w-[180px] focus:border-[#111827] focus:outline-none disabled:opacity-60"
                                                    testid={`dealer-lead-assign-footer-${lead.id}`}
                                                />
                                            </span>
                                        </>} />
                                )}
                                {open && <LeadQualityFooter lead={lead} />}
                            </div>
                        );
                    })}
                </div>
            )}
            {transferFor && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="lead-transfer-dialog">
                    <div className="bg-white rounded-sm border-2 border-[#111827] max-w-md w-full p-4 space-y-3 shadow-xl">
                        <div className="flex items-center justify-between gap-2">
                            <h3 className="font-black text-[#111827] inline-flex items-center gap-2 text-sm">
                                <MapPin className="w-4 h-4" /> Lead an anderen Standort abgeben
                            </h3>
                            <button type="button" onClick={() => setTransferFor(null)} className="text-gray-400 hover:text-[#111827]" data-testid="lead-transfer-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="text-xs text-gray-600">
                            Der Lead wird an die gewählte Filiale übergeben und ist dort neu zuzuweisen.
                            Es kann nur <b>abgegeben</b> werden — Leads anderer Standorte lassen sich nicht holen.
                        </div>
                        <select value={transferTarget} onChange={(e) => setTransferTarget(e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded-sm px-2 py-1.5 focus:border-[#111827] focus:outline-none"
                            data-testid="lead-transfer-target">
                            <option value="">— Ziel-Standort wählen —</option>
                            {transferTargets.filter((t) => t.id !== transferFor.assigned_dealer_id).map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                            ))}
                        </select>
                        <input value={transferNote} onChange={(e) => setTransferNote(e.target.value)}
                            placeholder="Notiz (optional), z.B. Grund der Abgabe"
                            className="w-full text-sm border border-gray-300 rounded-sm px-2 py-1.5 focus:border-[#111827] focus:outline-none"
                            data-testid="lead-transfer-note" />
                        <button type="button" disabled={!transferTarget || transferBusy} onClick={doTransfer}
                            className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-bold bg-[#111827] text-white rounded-sm px-3 py-2 hover:bg-black disabled:opacity-50"
                            data-testid="lead-transfer-submit">
                            {transferBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                            Abgeben
                        </button>
                    </div>
                </div>
            )}
            {callPrompt && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="call-outcome-dialog">
                    <div className="bg-white rounded-sm border-2 border-[#111827] max-w-md w-full p-4 space-y-3 shadow-xl">
                        <div className="flex items-center justify-between gap-2">
                            <h3 className="font-black text-[#111827] inline-flex items-center gap-2 text-sm">
                                <Phone className="w-4 h-4" /> Anruf beendet — Ereignis erfassen
                            </h3>
                            <button type="button" onClick={() => setCallPrompt(null)} className="text-gray-400 hover:text-[#111827]" data-testid="call-outcome-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="text-xs text-gray-600">
                            Wie lief das Telefonat mit <b>{callPrompt.seller?.name || callPrompt.subject || "dem Kunden"}</b>?
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {OUTCOME_OPTIONS.map(([k, label]) => (
                                <button key={k} type="button"
                                    onClick={async () => { const l = callPrompt; setCallPrompt(null); await handleEvent(l, k); }}
                                    className="text-[11px] font-bold px-2.5 py-1.5 rounded-sm border border-gray-300 bg-white hover:border-[#111827]"
                                    data-testid={`call-outcome-${k}`}>{label}</button>
                            ))}
                            <button type="button"
                                onClick={async () => { const l = callPrompt; setCallPrompt(null); await handleEvent(l, "status:won"); }}
                                className="text-[11px] font-bold px-2.5 py-1.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                data-testid="call-outcome-won">Gewonnen</button>
                            <button type="button"
                                onClick={async () => { const l = callPrompt; setCallPrompt(null); await handleEvent(l, "status:lost"); }}
                                className="text-[11px] font-bold px-2.5 py-1.5 rounded-sm border border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
                                data-testid="call-outcome-lost">Verloren…</button>
                        </div>
                        <button type="button" onClick={() => setCallPrompt(null)}
                            className="text-[11px] font-bold text-gray-500 hover:text-[#111827]" data-testid="call-outcome-later">
                            Später erfassen
                        </button>
                    </div>
                </div>
            )}
            {closeModal && (
                <CloseReasonModal
                    lead={closeModal.lead}
                    onClose={() => setCloseModal(null)}
                    onConfirm={async (reason) => {
                        const ok = await saveQuality(closeModal.lead, { status: "lost", close_reason: reason }, { silent: true });
                        if (ok) { toast.success("Lead als verloren geschlossen"); setCloseModal(null); }
                    }}
                />
            )}
            {showCreate && (
                <CreateLeadModal
                    onClose={() => setShowCreate(false)}
                    onCreated={() => { setShowCreate(false); load(); }}
                    brands={[...new Set((data?.items || []).map((l) => l.vehicle?.brand).filter(Boolean))].sort()}
                    sources={[...new Set((data?.items || []).map((l) => l.boerse).filter(Boolean))].sort()}
                />
            )}
            {showImport && (
                <ImportLeadsModal
                    onClose={() => setShowImport(false)}
                    onImported={() => { setShowImport(false); load(); }}
                />
            )}
            {showHandover && (
                <HandoverModal
                    user={user}
                    employees={employees}
                    onClose={() => setShowHandover(false)}
                    onDone={() => { setShowHandover(false); load(); }}
                />
            )}
        </div>
    );
}

// Aug-2026 (User-Wunsch) — Kundenwünsche & Fragen aus der Ursprungsnachricht (KI)
// Aug-2026 (User-Wunsch) — Kundenwünsche & Fragen des Kunden erscheinen jetzt
// direkt im Verlauf (Kommunikation & Notizen) als eigener Timeline-Eintrag.

// Aug-2026 (Prod-Debug) — erklärt bei leerer Liste, WO die Leads der
// Organisation liegen (Standort-Zuordnung) statt nur „keine Leads".
function LeadsEmptyDiagnostics() {
    const [diag, setDiag] = useState(null);
    useEffect(() => {
        api.get("/dealer/leads/diagnostics").then((r) => setDiag(r.data)).catch(() => {});
    }, []);
    if (!diag) return null;
    return (
        <div className="mt-4 max-w-xl mx-auto text-left text-xs bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="dealer-leads-diagnostics">
            {diag.org_total === 0 ? (
                <p className="text-amber-900">
                    In Ihrer Organisation sind derzeit <b>keine Leads</b> vorhanden. Leads entstehen
                    automatisch aus eingehenden E-Mail-Anfragen (Posteingang) oder manuell über
                    „Lead anlegen".{diag.unrouted > 0 && <> Achtung: <b>{diag.unrouted}</b> Lead(s) sind
                    keinem Standort zugeordnet — bitte E-Mail-Alias/Zuordnung prüfen.</>}
                </p>
            ) : (
                <div className="text-amber-900">
                    <p className="font-bold mb-1">
                        In Ihrer Organisation gibt es {diag.org_total} Lead(s) — Ihr Konto sieht davon {diag.visible}.
                    </p>
                    <ul className="space-y-0.5">
                        {diag.sites.map((s) => (
                            <li key={s.dealer_id} data-testid={`leads-diag-site-${s.label}`}>
                                {s.in_scope ? "✓" : "✗"} Standort <b>{s.label}</b>: {s.total} Lead(s) ({s.open} offen)
                                {!s.in_scope && " — außerhalb Ihres Standort-Zugriffs"}
                            </li>
                        ))}
                    </ul>
                    {diag.unrouted > 0 && <p className="mt-1">Zusätzlich {diag.unrouted} Lead(s) ohne Standort-Zuordnung.</p>}
                    {diag.visible === 0 && (
                        <p className="mt-1.5">
                            Hinweis: Filial-Konten sehen nur Leads des eigenen Standorts. Liegen alle Leads an
                            einem anderen Standort (z. B. Hauptsitz), prüfen Sie die E-Mail-Alias-Zuordnung
                            der eingehenden Anfragen oder melden Sie sich am Hauptsitz an.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

// Aug-2026 (User-Wunsch) — Urlaubs-Vertretung: alle offenen Leads eines
// Verkäufers gesammelt an eine Vertretung übergeben (mit Benachrichtigung).
function HandoverModal({ user, employees, onClose, onDone }) {
    const isEmp = user?.role === "zweiradverkaeufer";
    const [fromId, setFromId] = useState("");
    const [toId, setToId] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const empLabel = (e) => `${e.name}${e.absent ? ` (${e.absent_label || "abwesend"}${e.absent_until ? ` bis ${fmtDe(e.absent_until)}` : ""})` : ""}`;
    const submit = async () => {
        if (!toId) { toast.error("Bitte eine Vertretung wählen"); return; }
        if (!isEmp && !fromId) { toast.error("Bitte wählen, wessen Leads übergeben werden"); return; }
        setSaving(true);
        try {
            const { data: r } = await api.post("/dealer/leads/handover", {
                to_seller_id: toId,
                from_seller_id: isEmp ? undefined : fromId,
                note: note.trim() || undefined,
            });
            if (r.moved > 0) {
                toast.success(`${r.moved} offene Lead${r.moved === 1 ? "" : "s"} an ${r.to_seller_name} übergeben — Vertretung wurde benachrichtigt`);
            } else {
                toast.info(r.message || "Keine offenen Leads zum Übergeben");
            }
            if (r.warning) toast.warning(r.warning, { duration: 7000 });
            onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Übergabe fehlgeschlagen"); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="dealer-leads-handover-modal">
            <div className="bg-white rounded-sm w-full max-w-md p-5">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-black text-lg">Urlaubs-Vertretung</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="handover-close-btn"><X className="w-5 h-5" /></button>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                    Übergibt ALLE offenen Leads gesammelt an eine Vertretung — z. B. vor dem Urlaub.
                    Die Vertretung wird sofort benachrichtigt.
                </p>
                {!isEmp && (
                    <label className="block mb-3">
                        <span className="text-xs font-bold text-gray-700">Leads von (abwesender Verkäufer)</span>
                        <select value={fromId} onChange={(e) => setFromId(e.target.value)}
                            className="mt-1 w-full text-sm rounded-sm border border-gray-300 bg-white px-2 py-2 focus:border-[#111827] focus:outline-none"
                            data-testid="handover-from-select">
                            <option value="">— bitte wählen —</option>
                            {employees.map((e) => <option key={e.id} value={e.id}>{empLabel(e)}</option>)}
                        </select>
                    </label>
                )}
                <label className="block mb-3">
                    <span className="text-xs font-bold text-gray-700">Vertretung (übernimmt die Leads)</span>
                    <select value={toId} onChange={(e) => setToId(e.target.value)}
                        className="mt-1 w-full text-sm rounded-sm border border-gray-300 bg-white px-2 py-2 focus:border-[#111827] focus:outline-none"
                        data-testid="handover-to-select">
                        <option value="">— bitte wählen —</option>
                        {employees.filter((e) => e.id !== fromId).map((e) => <option key={e.id} value={e.id}>{empLabel(e)}</option>)}
                    </select>
                </label>
                <label className="block mb-4">
                    <span className="text-xs font-bold text-gray-700">Hinweis für die Vertretung (optional)</span>
                    <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300}
                        placeholder="z. B. Kunde Meier wartet auf Rückruf"
                        className="mt-1 w-full text-sm rounded-sm border border-gray-300 px-2 py-2 focus:border-[#111827] focus:outline-none"
                        data-testid="handover-note-input" />
                </label>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-sm font-bold border-2 border-gray-300 rounded-sm hover:bg-gray-50" data-testid="handover-cancel-btn">Abbrechen</button>
                    <button onClick={submit} disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold bg-[#111827] text-white rounded-sm hover:bg-black disabled:opacity-60"
                        data-testid="handover-submit-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Leads übergeben
                    </button>
                </div>
            </div>
        </div>
    );
}

const LEAD_FIELD_LABELS = {
    brand: "Marke", model: "Modell", year: "Baujahr", price: "Preis", mileage: "Laufleistung (km)",
    category: "Kategorie", condition: "Zustand", zip: "PLZ", city: "Ort",
    seller_name: "Verkäufer", seller_phone: "Telefon", seller_email: "E-Mail",
    boerse: "Börse/Portal", url: "Inserats-Link", subject: "Betreff", note: "Notiz",
};

function ImportLeadsModal({ onClose, onImported }) {
    const [file, setFile] = useState(null);
    const [headers, setHeaders] = useState([]);
    const [mapping, setMapping] = useState({});     // target -> header
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showMapping, setShowMapping] = useState(false);
    const [usedSaved, setUsedSaved] = useState(false);

    const downloadTemplate = async () => {
        try {
            const r = await api.get("/dealer/leads/import-template.csv", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = "Lead-Import-Vorlage.csv";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch { toast.error("Vorlage konnte nicht geladen werden"); }
    };

    const runPreview = async (f, map) => {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            if (map) fd.append("mapping", JSON.stringify(map));
            const r = await api.post("/dealer/leads/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    const onPick = async (f) => {
        setFile(f); setPreview(null); setHeaders([]); setMapping({}); setShowMapping(false); setUsedSaved(false);
        if (!f) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const insp = await api.post("/dealer/leads/import/inspect", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setHeaders(insp.data.headers || []);
            setUsedSaved(!!insp.data.used_saved_mapping);
            const map = insp.data.suggested_mapping || {};
            setMapping(map);
            await runPreview(f, map);
            // Mapping-Editor aufklappen, wenn Pflichtfelder nicht erkannt wurden
            if (!map.brand && !map.seller_name && !map.subject) setShowMapping(true);
        } catch (e) { toast.error(e?.response?.data?.detail || "Datei konnte nicht gelesen werden"); setBusy(false); }
    };

    const changeMapping = (target, header) => {
        const next = { ...mapping };
        if (header) next[target] = header; else delete next[target];
        setMapping(next);
        if (file) runPreview(file, next);
    };

    const doImport = async () => {
        if (!file || !preview?.new_count) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("mapping", JSON.stringify(mapping));
            const r = await api.post("/dealer/leads/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const skipped = r.data.skipped_duplicates || 0;
            toast.success(`${r.data.created} Lead(s) importiert${skipped ? ` · ${skipped} Dublette(n) übersprungen` : ""}`);
            onImported();
        } catch (e) { toast.error(e?.response?.data?.detail || "Import fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="dealer-leads-import-modal">
            <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 sticky top-0 bg-white">
                    <h2 className="font-black text-lg text-[#111827] flex items-center gap-2">
                        <Upload className="w-5 h-5 text-[#FFCC00]" /> Leads aus CSV importieren
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="dealer-leads-import-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-xs text-gray-500">
                        Lade eine CSV mit den Spalten der Vorlage hoch (Trennzeichen „;" oder „,"). Jede Zeile wird ein
                        dir zugewiesener Lead. Leere Zeilen werden übersprungen.
                    </p>
                    <button onClick={downloadTemplate} className="inline-flex items-center gap-2 text-sm font-bold text-[#111827] underline" data-testid="dealer-leads-import-template">
                        <Download className="w-4 h-4" /> CSV-Vorlage herunterladen
                    </button>
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-sm px-4 py-8 cursor-pointer hover:border-[#111827]" data-testid="dealer-leads-import-drop">
                        <FileUp className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">{file ? file.name : "CSV-Datei auswählen"}</span>
                        <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onPick(e.target.files?.[0] || null)} data-testid="dealer-leads-import-file" />
                    </label>

                    {busy && <div className="flex justify-center py-2"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}

                    {headers.length > 0 && (
                        <div className="border border-gray-200 rounded-sm" data-testid="dealer-leads-mapping">
                            <button type="button" onClick={() => setShowMapping((s) => !s)}
                                className="w-full flex items-center justify-between px-3 py-2 text-sm font-bold text-[#111827]"
                                data-testid="dealer-leads-mapping-toggle">
                                <span>Spalten-Zuordnung {Object.keys(mapping).length > 0 && <span className="text-gray-400 font-normal">({Object.keys(mapping).length} zugeordnet{usedSaved ? " · gespeichert" : ""})</span>}</span>
                                <ChevronDown className={`w-4 h-4 transition-transform ${showMapping ? "rotate-180" : ""}`} />
                            </button>
                            {showMapping && (
                                <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {Object.keys(LEAD_FIELD_LABELS).map((target) => (
                                        <div key={target} className="flex items-center gap-2 text-xs">
                                            <span className="w-28 shrink-0 text-gray-600">{LEAD_FIELD_LABELS[target]}{["brand"].includes(target) && " *"}</span>
                                            <select
                                                value={mapping[target] || ""}
                                                onChange={(e) => changeMapping(target, e.target.value)}
                                                className="flex-1 border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                data-testid={`dealer-leads-map-${target}`}>
                                                <option value="">— nicht zuordnen —</option>
                                                {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {preview && (
                        <div className="border border-gray-200 rounded-sm p-3" data-testid="dealer-leads-import-preview">
                            <div className="flex flex-wrap gap-3 text-sm mb-2">
                                <span className="font-bold text-emerald-700">{preview.new_count} neu</span>
                                {preview.duplicate_count > 0 && <span className="font-bold text-blue-600">{preview.duplicate_count} Dublette(n)</span>}
                                {preview.error_count > 0 && <span className="font-bold text-amber-600">{preview.error_count} übersprungen</span>}
                                {preview.valid_count > 20 && <span className="text-gray-500">Vorschau zeigt die ersten 20 Zeilen.</span>}
                            </div>
                            <div className="overflow-x-auto max-h-52">
                                <table className="w-full text-xs">
                                    <thead><tr className="text-left text-gray-500 border-b"><th className="py-1 pr-2">Marke</th><th className="py-1 pr-2">Modell</th><th className="py-1 pr-2">Preis</th><th className="py-1 pr-2">Ort</th><th className="py-1 pr-2">Verkäufer</th><th className="py-1 pr-2">Status</th></tr></thead>
                                    <tbody>
                                        {(preview.preview || []).map((r, i) => (
                                            <tr key={i} className="border-b border-gray-100">
                                                <td className="py-1 pr-2">{r.brand || "—"}</td>
                                                <td className="py-1 pr-2">{r.model || "—"}</td>
                                                <td className="py-1 pr-2 font-mono-num">{r.price ?? "—"}</td>
                                                <td className="py-1 pr-2">{[r.zip, r.city].filter(Boolean).join(" ") || "—"}</td>
                                                <td className="py-1 pr-2">{r.seller_name || "—"}</td>
                                                <td className="py-1 pr-2">{r.duplicate
                                                    ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200">Dublette</span>
                                                    : <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">neu</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {(preview.errors || []).length > 0 && (
                                <ul className="mt-2 text-[11px] text-amber-700 list-disc pl-4">
                                    {preview.errors.slice(0, 5).map((er, i) => <li key={i}>Zeile {er.row}: {er.error}</li>)}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="rounded-sm border border-gray-300 px-4 py-2 text-sm font-bold hover:bg-gray-50" data-testid="dealer-leads-import-cancel">Abbrechen</button>
                    <button onClick={doImport} disabled={busy || !preview || preview.new_count === 0} className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] rounded-sm px-4 py-2 text-sm font-bold hover:brightness-95 disabled:opacity-60" data-testid="dealer-leads-import-commit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {preview ? `${preview.new_count} importieren` : "Importieren"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Aug-2026 (User-Wunsch) — Vermerke & Wiedervorlage sind in den Verlauf umgezogen
// (Notiz-Eingabe + Wiedervorlage-Datum direkt im Timeline-Panel). Hier bleibt nur
// noch der Schließ-Grund geschlossener Leads sichtbar.
function LeadQualityFooter({ lead }) {
    const closed = lead.status === "lost" || lead.status === "archived" || lead.status === "won";
    if (!(closed && lead.close_reason)) return null;
    return (
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600" data-testid={`dealer-lead-close-reason-${lead.id}`}>
            <span className="font-bold">Schließ-Grund:</span> {lead.close_reason}
        </div>
    );
}

function CloseReasonModal({ lead, onClose, onConfirm }) {
    const [reason, setReason] = useState(lead.close_reason || "");
    const [saving, setSaving] = useState(false);
    const PRESETS = ["Zu teuer", "Anderweitig gekauft", "Kein Interesse mehr", "Nicht erreichbar", "Fahrzeug verkauft", "Falsche Daten", "Keine Schufa", "Spam"];
    const confirm = async () => {
        if (!reason.trim()) { toast.error("Bitte einen Schließ-Grund angeben"); return; }
        setSaving(true);
        await onConfirm(reason.trim());
        setSaving(false);
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="dealer-lead-close-modal">
            <div className="bg-white rounded-sm w-full max-w-md">
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
                    <h2 className="font-black text-lg text-[#111827] flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" /> Lead als verloren schließen
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="dealer-lead-close-cancel-x"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <p className="text-xs text-gray-500">Ein Schließ-Grund ist erforderlich, um den Lead zu schließen. Das hilft bei der Auswertung verlorener Leads.</p>
                    <div className="flex flex-wrap gap-1.5">
                        {PRESETS.map((p) => (
                            <button key={p} onClick={() => setReason(p)} className={`text-[11px] font-medium rounded-full border px-2.5 py-1 transition-colors ${reason === p ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827]"}`} data-testid={`dealer-lead-close-preset-${p}`}>{p}</button>
                        ))}
                    </div>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        placeholder="Grund eingeben oder oben auswählen …"
                        className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:border-[#111827] focus:outline-none"
                        data-testid="dealer-lead-close-reason-input"
                        autoFocus
                    />
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
                    <button onClick={onClose} className="rounded-sm border border-gray-300 px-4 py-2 text-sm font-bold hover:bg-gray-50" data-testid="dealer-lead-close-cancel">Abbrechen</button>
                    <button onClick={confirm} disabled={saving} className="inline-flex items-center gap-2 bg-gray-800 text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-black disabled:opacity-60" data-testid="dealer-lead-close-confirm">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Verloren schließen
                    </button>
                </div>
            </div>
        </div>
    );
}

function CreateLeadModal({ onClose, onCreated, brands = [], sources = [] }) {
    const [form, setForm] = useState({
        brand: "", model: "", year: "", price: "", mileage: "", category: "motorcycle",
        condition: "Gebrauchtfahrzeug",
        zip: "", city: "", seller_name: "", seller_phone: "", seller_email: "", boerse: "", url: "", note: "",
        reason: "",
    });
    const [saving, setSaving] = useState(false);
    // Aug-2026 (User-Wunsch) — auf vorhandene Systemdaten zurückgreifen:
    // Bestandskunden-Suche (CRM) füllt Name/Telefon/E-Mail/PLZ/Stadt automatisch.
    const [custResults, setCustResults] = useState([]);
    const custPicked = useRef(false);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    useEffect(() => {
        const q = form.seller_name.trim();
        if (custPicked.current) { custPicked.current = false; return; }
        if (q.length < 2) { setCustResults([]); return; }
        const t = setTimeout(() => {
            api.get(`/dealer/customers/search?q=${encodeURIComponent(q)}`)
                .then((r) => setCustResults((r.data.customers || []).slice(0, 6)))
                .catch(() => setCustResults([]));
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.seller_name]);

    const pickCustomer = (c) => {
        custPicked.current = true;
        const name = c.company_name || [c.first_name, c.last_name].filter(Boolean).join(" ");
        setForm((p) => ({
            ...p,
            seller_name: name || p.seller_name,
            seller_phone: c.phone || p.seller_phone,
            seller_email: c.email || p.seller_email,
            zip: c.zip || c.postal_code || p.zip,
            city: c.city || p.city,
        }));
        setCustResults([]);
        toast.success(`Bestandskunde übernommen: ${name}`);
    };

    const submit = async () => {
        if (!form.brand.trim()) { toast.error("Bitte mindestens die Marke angeben"); return; }
        setSaving(true);
        try {
            const payload = {
                brand: form.brand.trim() || null,
                model: form.model.trim() || null,
                year: form.year ? parseInt(form.year, 10) : null,
                price: form.price ? parseFloat(form.price) : null,
                mileage: form.mileage ? parseInt(form.mileage, 10) : null,
                category: form.category || null,
                condition: form.condition || null,
                zip: form.zip.trim() || null,
                city: form.city.trim() || null,
                seller_name: form.seller_name.trim() || null,
                seller_phone: form.seller_phone.trim() || null,
                seller_email: form.seller_email.trim() || null,
                boerse: form.boerse.trim() || null,
                url: form.url.trim() || null,
                note: form.note.trim() || null,
                reason: form.reason || null,
            };
            await api.post("/dealer/leads", payload);
            toast.success("Lead angelegt — jetzt umwandelbar");
            onCreated();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="dealer-lead-create-modal">
            <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 sticky top-0 bg-white">
                    <h2 className="font-black text-lg text-[#111827] flex items-center gap-2">
                        <Plus className="w-5 h-5 text-[#FFCC00]" /> Laden-Kunden erfassen
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="lead-create-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-xs text-gray-500">Der Lead wird Ihnen direkt zugewiesen und kann sofort „in Kauf umgewandelt" werden.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Marke *</span>
                            <input value={form.brand} onChange={(e) => set("brand", e.target.value)} list="lead-brand-list"
                                placeholder="z. B. BMW" className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:border-[#111827] focus:outline-none"
                                data-testid="lead-field-brand" />
                            <datalist id="lead-brand-list">
                                {brands.map((b) => <option key={b} value={b} />)}
                            </datalist>
                        </label>
                        <LeadInput label="Modell" k="model" value={form.model} onChange={set} placeholder="z. B. R 1250 GS" />
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Fahrzeugart</span>
                            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm bg-white" data-testid="lead-field-category">
                                <option value="motorcycle">Motorrad</option>
                                <option value="bicycle">Fahrrad</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Zustand</span>
                            <select value={form.condition} onChange={(e) => set("condition", e.target.value)} className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm bg-white" data-testid="lead-field-condition">
                                <option value="Neufahrzeug">Neufahrzeug</option>
                                <option value="Tageszulassung">Tageszulassung</option>
                                <option value="Vorführfahrzeug">Vorführfahrzeug</option>
                                <option value="Gebrauchtfahrzeug">Gebrauchtfahrzeug</option>
                            </select>
                        </label>
                        <LeadInput label="Erstzulassung (Jahr)" k="year" value={form.year} onChange={set} type="number" placeholder="2022" />
                        <LeadInput label="Preis (€)" k="price" value={form.price} onChange={set} type="number" placeholder="12500" />
                        <LeadInput label="Kilometerstand" k="mileage" value={form.mileage} onChange={set} type="number" placeholder="8500" />
                        <LeadInput label="PLZ" k="zip" value={form.zip} onChange={set} placeholder="80331" />
                        <LeadInput label="Stadt" k="city" value={form.city} onChange={set} placeholder="München" />
                        <label className="block relative">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kunde (Name) — Bestandskunden-Suche</span>
                            <input value={form.seller_name} onChange={(e) => set("seller_name", e.target.value)}
                                placeholder="Tippen sucht im Kundenstamm…" autoComplete="off"
                                className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:border-[#111827] focus:outline-none"
                                data-testid="lead-field-seller-name" />
                            {custResults.length > 0 && (
                                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-sm shadow-lg max-h-48 overflow-auto" data-testid="lead-customer-suggestions">
                                    {custResults.map((c) => {
                                        const nm = c.company_name || [c.first_name, c.last_name].filter(Boolean).join(" ");
                                        return (
                                            <button type="button" key={c.id || c.customer_number || nm}
                                                onMouseDown={(e) => { e.preventDefault(); pickCustomer(c); }}
                                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 border-b border-gray-100 last:border-0"
                                                data-testid="lead-customer-suggestion">
                                                <span className="font-bold">{nm}</span>
                                                <span className="text-gray-500"> {[c.email, c.phone, c.city].filter(Boolean).join(" · ")}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </label>
                        <LeadInput label="Telefon" k="seller_phone" value={form.seller_phone} onChange={set} placeholder="+49 …" />
                        <LeadInput label="E-Mail" k="seller_email" value={form.seller_email} onChange={set} placeholder="kunde@mail.de" />
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Quelle / Börse</span>
                            <input value={form.boerse} onChange={(e) => set("boerse", e.target.value)} list="lead-source-list"
                                placeholder="z. B. mobile.de" className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:border-[#111827] focus:outline-none"
                                data-testid="lead-field-boerse" />
                            <datalist id="lead-source-list">
                                {sources.map((s) => <option key={s} value={s} />)}
                            </datalist>
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Grund / Anlass</span>
                            <select value={form.reason} onChange={(e) => set("reason", e.target.value)} className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm bg-white" data-testid="lead-field-reason">
                                <option value="">— wählen —</option>
                                <option>Homepage-Anfrage</option>
                                <option>Anruf</option>
                                <option>E-Mail-Anfrage</option>
                                <option>Laufkundschaft</option>
                                <option>Empfehlung</option>
                                <option>Messe / Event</option>
                                <option>Finanzierung läuft aus</option>
                                <option>Leasing läuft aus</option>
                                <option>Sonstiges</option>
                            </select>
                        </label>
                        <LeadInput label="Original-Link" k="url" value={form.url} onChange={set} placeholder="https://…" />
                    </div>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Notiz</span>
                        <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:border-[#111827] focus:outline-none" data-testid="lead-field-note" />
                    </label>
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="rounded-sm border border-gray-300 px-4 py-2 text-sm font-bold hover:bg-gray-50" data-testid="lead-create-cancel">Abbrechen</button>
                    <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] rounded-sm px-4 py-2 text-sm font-bold hover:brightness-95 disabled:opacity-60" data-testid="lead-create-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Lead anlegen
                    </button>
                </div>
            </div>
        </div>
    );
}

// Aug-2026 (User-Wunsch) — „Kurzinfo für Mitarbeiter" ist entfallen: solche
// Hinweise werden direkt als Notiz im Verlauf (Kommunikation & Notizen) erfasst.

// SMS-Verlauf am Lead (Jul-2026, User-Wunsch): gesendete SMS/WhatsApp + eingehende
// Kunden-Antworten (Twilio-Webhook) chronologisch als Thread.

// Kontakt-Buttons (User-Wunsch Jul-2026): Anruf, WhatsApp, SMS, E-Mail direkt am Lead —
// plus „Rufnummer / E-Mail falsch"-Markierung und Nummern-Recherche über Telefonbuch-Portale.
function ContactButtons({ lead, assistantActive, onAssistant, onCall }) {
    const [flags, setFlags] = useState(lead.contact_flags || {});
    const [busy, setBusy] = useState(false);
    const [vidBusy, setVidBusy] = useState(false);
    const phone = (lead.seller?.phone || "").trim();
    const email = (lead.seller?.email || lead.from_email || "").trim();

    // Jun-2026 (User-Wunsch) — Video-Beratung direkt aus der Lead-Bearbeitung
    const startVideo = async () => {
        if (vidBusy) return;
        setVidBusy(true);
        try {
            const r = await api.post("/dealer/video-meetings", {
                title: `Beratung: ${(lead.seller?.name || lead.subject || "Lead").slice(0, 70)}`,
                customer_name: lead.seller?.name || null,
                customer_email: email || null,
                lead_id: lead.id,
            });
            const m = r.data;
            try { await navigator.clipboard?.writeText(m.public_url || ""); } catch { /* optional */ }
            window.open(`/beratung/${m.token}`, "_blank", "noopener");
            toast.success("Video-Beratung erstellt — Kunden-Link in der Zwischenablage");
        } catch (e) { toast.error(e?.response?.data?.detail || "Video-Beratung konnte nicht erstellt werden"); }
        setVidBusy(false);
    };

    let wa = phone.replace(/[^\d+]/g, "");
    if (wa.startsWith("00")) wa = wa.slice(2);
    else if (wa.startsWith("0")) wa = `49${wa.slice(1)}`;
    wa = wa.replace(/^\+/, "");
    const msg = encodeURIComponent(`Guten Tag${lead.seller?.name ? ` ${lead.seller.name}` : ""}, hier ist Ihr Zweirad-Händler. Wann dürfen wir Sie kurz sprechen?`);
    const subject = encodeURIComponent(lead.subject || "Ihre Anfrage");
    const btn = "inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-sm border transition-colors shrink-0 whitespace-nowrap";

    const setFlag = async (patch) => {
        setBusy(true);
        try {
            const r = await api.patch(`/dealer/leads/${lead.id}/contact-flags`, patch);
            setFlags(r.data.contact_flags || {});
            toast.success("Markierung gespeichert — als Vermerk protokolliert");
        } catch (e) { toast.error(e?.response?.data?.detail || "Konnte Markierung nicht speichern"); }
        setBusy(false);
    };

    const who = encodeURIComponent(`${lead.seller?.name || ""} ${lead.location?.city || ""}`.trim());
    const nameSeg = encodeURIComponent((lead.seller?.name || "").trim() || "-");
    const citySeg = encodeURIComponent((lead.location?.city || "").trim() || "-");
    const needsResearch = !phone || flags.phone_wrong || !email || flags.email_wrong;

    return (
        <div className="mt-2 space-y-1.5" data-testid={`dealer-lead-contact-${lead.id}`}>
            {/* Aug-2026 (User-Wunsch) — alle Kontakt-Aktionen in EINER Zeile */}
            <div className="flex flex-nowrap items-center gap-1.5 overflow-x-auto pb-0.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-0.5 shrink-0">Kontakt:</span>
                {onAssistant && (
                    <button type="button" onClick={onAssistant}
                        className={`${btn} ${assistantActive ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "border-gray-300 bg-white text-[#111827] hover:border-[#111827]"}`}
                        title="KI-Coach: Einschätzung, Einwandsbehandlung & kompletter Kommunikationsverlauf"
                        data-testid={`dealer-lead-assistant-btn-${lead.id}`}>
                        <Sparkles className="w-3 h-3" /> KI-Coach &amp; Verlauf
                    </button>
                )}
                <button type="button" onClick={startVideo} disabled={vidBusy}
                    className={`${btn} border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50`}
                    title="Video-Beratung starten: Raum erstellen, Kunden-Link kopieren und beitreten"
                    data-testid={`lead-contact-video-${lead.id}`}>
                    {vidBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Video className="w-3 h-3" />} Video
                </button>
                {phone && !flags.phone_wrong && (
                    <>
                        <a href={`tel:${phone}`} onClick={() => onCall && onCall()} className={`${btn} border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100`} data-testid={`lead-contact-call-${lead.id}`}>
                            <Phone className="w-3 h-3" /> Anruf
                        </a>
                        <a href={`https://wa.me/${wa}?text=${msg}`} target="_blank" rel="noreferrer" className={`${btn} border-green-200 bg-green-50 text-green-800 hover:bg-green-100`} data-testid={`lead-contact-whatsapp-${lead.id}`}>
                            <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                        <a href={`sms:${phone}?body=${msg}`} className={`${btn} border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100`} data-testid={`lead-contact-sms-${lead.id}`}>
                            <MessageSquare className="w-3 h-3" /> SMS
                        </a>
                    </>
                )}
                {phone && flags.phone_wrong && (
                    <span className="text-[11px] text-red-700 line-through" title="Als falsch markiert" data-testid={`lead-phone-wrong-${lead.id}`}>{phone}</span>
                )}
                {phone && (
                    <button type="button" disabled={busy} onClick={() => setFlag({ phone_wrong: !flags.phone_wrong })}
                        className={`${btn} ${flags.phone_wrong ? "border-gray-300 bg-white text-gray-600" : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"}`}
                        title={flags.phone_wrong ? "Rufnummer doch korrekt" : "Rufnummer als falsch markieren"}
                        data-testid={`lead-flag-phone-${lead.id}`}>
                        <X className="w-3 h-3" /> {flags.phone_wrong ? "Nr. doch korrekt" : "Rufnummer falsch"}
                    </button>
                )}
                {email && !flags.email_wrong && (
                    <a href={`mailto:${email}?subject=${subject}`} className={`${btn} border-gray-300 bg-white text-gray-800 hover:bg-gray-50`} data-testid={`lead-contact-email-${lead.id}`}>
                        <Mail className="w-3 h-3" /> E-Mail
                    </a>
                )}
                {email && flags.email_wrong && (
                    <span className="text-[11px] text-red-700 line-through" title="Als falsch markiert">{email}</span>
                )}
                {email && (
                    <button type="button" disabled={busy} onClick={() => setFlag({ email_wrong: !flags.email_wrong })}
                        className={`${btn} ${flags.email_wrong ? "border-gray-300 bg-white text-gray-600" : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"}`}
                        title={flags.email_wrong ? "E-Mail doch korrekt" : "E-Mail als falsch markieren"}
                        data-testid={`lead-flag-email-${lead.id}`}>
                        <X className="w-3 h-3" /> {flags.email_wrong ? "E-Mail doch korrekt" : "E-Mail falsch"}
                    </button>
                )}
                {!phone && !email && <span className="text-[11px] text-gray-500">keine Kontaktdaten hinterlegt</span>}
            </div>
            {needsResearch && lead.seller?.name && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid={`lead-research-${lead.id}`}>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-0.5">Nummer suchen:</span>
                    <a href={`https://www.dasoertliche.de/?kw=${who}`} target="_blank" rel="noreferrer" className={`${btn} border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100`} data-testid={`lead-research-oertliche-${lead.id}`}>Das Örtliche</a>
                    <a href={`https://www.dastelefonbuch.de/Suche/${nameSeg}/${citySeg}`} target="_blank" rel="noreferrer" className={`${btn} border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100`}>Das Telefonbuch</a>
                    <a href={`https://www.11880.com/suche/${nameSeg}/${citySeg}`} target="_blank" rel="noreferrer" className={`${btn} border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100`}>11880</a>
                    <a href={`https://www.google.com/search?q=${who}%20Telefonnummer`} target="_blank" rel="noreferrer" className={`${btn} border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100`}>Google</a>
                </div>
            )}
        </div>
    );
}

